import { useState, type FormEvent } from 'react';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const SUBMIT_URL = 'https://readdy.ai/api/form/db96q3jk13ra8mig9220';

export default function ContactPage() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useDocumentMeta({
    title: '联系我们 - 91吃瓜',
    description: '通过联系表单与 91吃瓜 团队取得联系，提供线索、建议或合作意向，我们会尽快回复。',
    keywords: '91吃瓜,联系我们,线索投递,合作',
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const guard = String(formData.get('website_alt') ?? '').trim();
    if (guard) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string' && value.trim() === '') return;
      payload.append(key, value);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam =
        typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const ok =
        response.ok &&
        parsed?.code === 'OK' &&
        !isSpam;

      if (ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后再试');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
      <header>
        <span className="inline-flex items-center gap-2 text-primary-600 text-sm font-medium">
          <span className="w-6 h-[2px] bg-primary-500" />
          联系我们
        </span>
        <h1 className="mt-3 font-heading font-black text-3xl md:text-4xl text-foreground-950">
          有话想对我们说？
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl">
          无论是提供线索、内容建议还是商务合作，都可以通过下面的表单告诉我们。
          我们会认真阅读每一条留言。
        </p>
      </header>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
        <div className="lg:col-span-2">
          <form
            data-readdy-form
            id="chigua91-contact-form"
            onSubmit={handleSubmit}
            className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-7"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  姓名 <span className="text-accent-500">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入你的称呼"
                  className="w-full h-11 px-3 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  邮箱 <span className="text-accent-500">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder="name@example.com"
                  className="w-full h-11 px-3 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="contact-type" className="block text-sm font-medium text-foreground-800 mb-1.5">
                留言类型
              </label>
              <select
                id="contact-type"
                name="type"
                defaultValue="线索投递"
                className="w-full h-11 px-3 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
              >
                <option value="线索投递">线索投递</option>
                <option value="内容建议">内容建议</option>
                <option value="商务合作">商务合作</option>
                <option value="其他">其他</option>
              </select>
            </div>

            <div className="mt-4">
              <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                留言内容 <span className="text-accent-500">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={6}
                placeholder="请描述你的想法（最多 500 字）"
                className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-none"
              />
              <p className="mt-1.5 text-xs text-foreground-400">最多 500 个字符</p>
            </div>

            <input
              type="text"
              name="website_alt"
              className="readdy-field-guard"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="mt-6 flex items-center gap-4 flex-wrap">
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    提交中
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" />
                    提交留言
                  </>
                )}
              </button>
              <span className="text-xs text-foreground-400">带 * 为必填项，我们不会公开你的邮箱</span>
            </div>

            {status === 'success' && (
              <div className="mt-5 flex items-start gap-3 p-4 rounded-md bg-secondary-100 border border-secondary-200">
                <i className="ri-checkbox-circle-line text-xl text-secondary-700 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-secondary-900">提交成功</p>
                  <p className="mt-1 text-xs text-secondary-800">
                    感谢你的留言，我们已经收到，会尽快通过邮箱与你联系。
                  </p>
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="mt-5 flex items-start gap-3 p-4 rounded-md bg-accent-100 border border-accent-200">
                <i className="ri-error-warning-line text-xl text-accent-700 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-accent-900">提交失败</p>
                  <p className="mt-1 text-xs text-accent-800">{formError}</p>
                </div>
              </div>
            )}
          </form>
        </div>

        <aside className="flex flex-col gap-4">
          <div className="rounded-lg bg-background-50 border border-background-200 p-5">
            <h2 className="font-heading font-bold text-lg text-foreground-950">联系方式</h2>
            <ul className="mt-4 flex flex-col gap-4">
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 shrink-0 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
                  <i className="ri-mail-line" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">邮箱</p>
                  <p className="mt-0.5 text-sm text-foreground-500">hello@chigua91.demo</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 shrink-0 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
                  <i className="ri-weibo-line" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">官方微博</p>
                  <p className="mt-0.5 text-sm text-foreground-500">@91吃瓜</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 shrink-0 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
                  <i className="ri-wechat-line" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">微信公众号</p>
                  <p className="mt-0.5 text-sm text-foreground-500">91吃瓜资讯</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 shrink-0 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
                  <i className="ri-time-line" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">回复时间</p>
                  <p className="mt-0.5 text-sm text-foreground-500">工作日 10:00 - 19:00</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="rounded-lg bg-secondary-100 border border-secondary-200 p-5">
            <h2 className="font-heading font-bold text-base text-secondary-900">温馨提示</h2>
            <p className="mt-2 text-sm text-secondary-800 leading-relaxed">
              本站内容为演示资讯，留言表单为可正常提交的平台表单。
              请勿在留言中填写身份证号、银行卡等敏感信息。
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}